// SlateReviews.tsx — (tf3-40; Creative 2 C2.1-1 §2, planning 10-21 15:05) the audience-first slate's two reviews, on its
// board: which interests to develop (keep up to three, or add your own; the rest are dropped), then which interest × format
// pairs become ideas (1–4). Send is off outside those bounds, and says why. A refusal is shown in words:
//   GATE_REFUSED  saved, but the engine didn't move on: "Send again" with the same body (the workers add nothing twice);
//   CONFLICT / UNKNOWN_ITEM  the board is read again, and the choices whose ids remain are kept.
import { useEffect, useState } from "react";
import { TfCard, Btn } from "@/components/v2/primitives.tsx";
import { engineSlatesApi } from "@/api/engine-slates.ts";
import { INTERESTS_HEADING, INTERESTS_RULE, INTERESTS_MAX, PAIRS_HEADING, PAIRS_MAX, evidenceLine, interestsBody, interestsCount, pairsBody, pairsByInterest, stillThere,
  rereadAfter, slateRefusalText, type AddedInterest, type SlateView } from "@/lib/slates.ts";

const red = { color: "#b91c1c", fontSize: 13, marginTop: 6 } as const;
type Sent = { code?: string; text: string };

/** The send and its refusal, shared by both reviews: a GATE_REFUSED keeps the body for "Send again". */
function useSend<B>(send: (b: B) => Promise<unknown>, onSent: () => void, onReread: () => void) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Sent | null>(null);
  const [again, setAgain] = useState<B | null>(null);
  const go = async (b: B) => {
    setBusy(true); setErr(null); setAgain(null);
    try { await send(b); onSent(); }
    catch (e) {   // catch-reason: shown under the review in words; a GATE_REFUSED offers Send again, a CONFLICT reads the board again
      const code = (e as { code?: string }).code;
      setErr({ code, text: slateRefusalText(e) });
      if (code === "GATE_REFUSED") setAgain(b);
      if (rereadAfter(code)) onReread();
    } finally { setBusy(false); }
  };
  return { busy, err, again, go };
}

export function InterestsReview({ s, onSent, onReread }: { s: SlateView; onSent: () => void; onReread: () => void }) {
  const interests = [...(s.interests ?? [])].sort((a, b) => a.position - b.position);
  const ids = interests.map((i) => i.interestId);
  const [keep, setKeep] = useState<string[]>(() => interests.filter((i) => i.kept === true).map((i) => i.interestId));
  const [add, setAdd] = useState<AddedInterest[]>([]);
  const [reason, setReason] = useState("");
  const idsKey = ids.join(",");
  useEffect(() => { setKeep((k) => stillThere(k, idsKey ? idsKey.split(",") : [])); }, [idsKey]);   // after a re-read: what remains
  const { busy, err, again, go } = useSend((b: { keep: string[]; add: AddedInterest[]; reason?: string }) => engineSlatesApi.interests(s.slateId, b), onSent, onReread);
  const r = interestsBody(keep, add, reason);
  const toggle = (id: string) => setKeep((k) => (k.includes(id) ? k.filter((x) => x !== id) : [...k, id]));
  return (
    <TfCard>
      <h3 style={{ marginTop: 0 }}>{INTERESTS_HEADING}</h3>
      <div className="tf-muted" style={{ fontSize: 13, marginBottom: 8 }}>{INTERESTS_RULE}</div>
      {interests.map((i) => (
        <label key={i.interestId} style={{ display: "block", fontSize: 13, margin: "6px 0" }}>
          <input type="checkbox" checked={keep.includes(i.interestId)} onChange={() => toggle(i.interestId)} />{" "}
          <strong>{i.interest}</strong>{i.origin === "yours" ? " (yours)" : ""} — {i.reason}
          {i.evidence.length > 0 && <ul style={{ margin: "2px 0 0 22px", fontSize: 12 }} className="tf-muted">{i.evidence.map((e, k) => <li key={k}>{evidenceLine(e)}</li>)}</ul>}
        </label>))}
      {add.map((a, k) => (
        <div key={k} style={{ display: "flex", gap: 6, margin: "6px 0", fontSize: 13 }}>
          <input placeholder="An interest of your own" maxLength={80} value={a.interest} onChange={(e) => setAdd(add.map((x, j) => (j === k ? { ...x, interest: e.target.value } : x)))} style={{ flex: 1 }} />
          <input placeholder="Why this audience cares" maxLength={240} value={a.reason} onChange={(e) => setAdd(add.map((x, j) => (j === k ? { ...x, reason: e.target.value } : x)))} style={{ flex: 2 }} />
          <Btn size="sm" variant="ghost" onClick={() => setAdd(add.filter((_, j) => j !== k))}>Remove</Btn>
        </div>))}
      {keep.length + add.length < INTERESTS_MAX && <Btn size="sm" variant="ghost" onClick={() => setAdd([...add, { interest: "", reason: "" }])}>Add one of your own</Btn>}
      <div style={{ fontSize: 13, marginTop: 8 }}>{interestsCount(keep, add)} of {INTERESTS_MAX} at most.</div>
      <input placeholder="Why (optional)" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: "100%", marginTop: 6 }} />
      <div style={{ marginTop: 8 }}>
        <Btn onClick={() => "body" in r && void go(r.body)} disabled={busy || !("body" in r)}>{busy ? "Sending…" : "Send the interests"}</Btn>
        {"off" in r && <span className="tf-muted" style={{ fontSize: 12, marginLeft: 8 }}>{r.off}</span>}
      </div>
      {err && <div style={red}>{err.text}</div>}
      {again && <Btn size="sm" onClick={() => void go(again)} disabled={busy}>Send again</Btn>}
    </TfCard>
  );
}

export function PairsReview({ s, onSent, onReread }: { s: SlateView; onSent: () => void; onReread: () => void }) {
  const groups = pairsByInterest(s.interests ?? [], s.strategies ?? []);
  const ids = (s.strategies ?? []).map((x) => x.strategyId);
  const [choose, setChoose] = useState<string[]>(() => (s.strategies ?? []).filter((x) => x.chosen === true).map((x) => x.strategyId));
  const [reason, setReason] = useState("");
  const idsKey = ids.join(",");
  useEffect(() => { setChoose((c) => stillThere(c, idsKey ? idsKey.split(",") : [])); }, [idsKey]);
  const { busy, err, again, go } = useSend((b: { choose: string[]; reason?: string }) => engineSlatesApi.strategies(s.slateId, b), onSent, onReread);
  const r = pairsBody(choose, reason);
  const toggle = (id: string) => setChoose((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  return (
    <TfCard>
      <h3 style={{ marginTop: 0 }}>{PAIRS_HEADING}</h3>
      <div className="tf-muted" style={{ fontSize: 13, marginBottom: 8 }}>Choose 1–{PAIRS_MAX}; the rest are dropped.</div>
      {groups.map((g) => (
        <div key={g.interestId} style={{ margin: "8px 0" }}>
          <div style={{ fontSize: 13 }}><strong>{g.interest?.interest ?? g.interestId}</strong></div>
          {g.pairs.map((p) => (
            <label key={p.strategyId} style={{ display: "block", fontSize: 13, margin: "4px 0 4px 12px" }}>
              <input type="checkbox" checked={choose.includes(p.strategyId)} onChange={() => toggle(p.strategyId)} />{" "}
              <strong>{p.family.replace(/_/g, " ")}</strong> — {p.reason} <span className="tf-muted">(patterns: {p.patternIds.join(", ")})</span>
              {p.evidence.length > 0 && <ul style={{ margin: "2px 0 0 22px", fontSize: 12 }} className="tf-muted">{p.evidence.map((e, k) => <li key={k}>{evidenceLine(e)}</li>)}</ul>}
            </label>))}
        </div>))}
      <div style={{ fontSize: 13, marginTop: 8 }}>{choose.length} of {PAIRS_MAX} at most.</div>
      <input placeholder="Why (optional)" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: "100%", marginTop: 6 }} />
      <div style={{ marginTop: 8 }}>
        <Btn onClick={() => "body" in r && void go(r.body)} disabled={busy || !("body" in r)}>{busy ? "Sending…" : "Send the pairs"}</Btn>
        {"off" in r && <span className="tf-muted" style={{ fontSize: 12, marginLeft: 8 }}>{r.off}</span>}
      </div>
      {err && <div style={red}>{err.text}</div>}
      {again && <Btn size="sm" onClick={() => void go(again)} disabled={busy}>Send again</Btn>}
    </TfCard>
  );
}
